/* eslint-disable complexity */
import { useState, useMemo, useContext } from 'react';
import { Separator } from '@/components/ui/separator';

import {
  QuoteTypeToLabel,
  PolicyTypeToLabel,
  TransactionStatusToLabel,
} from '@/lib/pages/forms/fields';
import { formatDate } from '@/lib/utils/dates';

import type { Policy, TransactionSummaryData } from '@/lib/heyapi/types.gen';
import { TransactionStatus, TransactionType } from '@/lib/heyapi/types.gen';

import { formatCurrency } from '@/lib/utils/currency';

import { getCleanText, TEXT_PLACEHOLDER } from '../utils';
import type { Message } from './StandardMessage';
import { StandardMessageHeader } from './StandardMessage';
import { DropdownMenuButton } from './DropdownMenuButton';
import { glacierPolicyApiGetLatestTransactionLatestAttemptOptions } from '@/lib/heyapi/@tanstack/react-query.gen';
import { useQuery } from '@tanstack/react-query';
import { PermissionsContext } from '../../PermissionContext';
import { History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { transactionTypeDisplayText } from '@/lib/components/cells';
import { snakeToTitleCase } from '@/lib/utils';
import { DebugTransaction } from './DropdownComponents/DebugTransaction';
import { Dialog, DialogContent } from '@/components/ui/dialog';

type RatingMetadata = {
  proration_str: string;
};

type RatingResponse = {
  metadata: RatingMetadata;
};

export const HeaderView = ({
  policy,
  latestTransactionSummary,
}: {
  policy: Policy;
  latestTransactionSummary: TransactionSummaryData | undefined;
}) => {
  const [message, setMessage] = useState<Message | undefined>(undefined);
  const { isTravelTime } = useContext(PermissionsContext);
  const [openDebugDialog, setOpenDebugDialog] = useState(false);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const { data: latestRatingAttempt } = useQuery({
    ...glacierPolicyApiGetLatestTransactionLatestAttemptOptions({
      path: {
        account_id: policy.pk!,
      },
    }),
  });

  const effectiveDate = useMemo(() => {
    return formatDate(policy?.details?.policyEffectiveDate) || TEXT_PLACEHOLDER;
  }, [policy?.details?.policyEffectiveDate]);

  const transactionEffectiveDate = useMemo(() => {
    return (
      formatDate(policy?.latestTransaction?.effectiveDate) || TEXT_PLACEHOLDER
    );
  }, [policy?.latestTransaction?.effectiveDate]);

  const expirationDate = useMemo(() => {
    if (
      policy?.latestTransaction?.transactionType ===
        TransactionType.POLICY_CANCELLED &&
      policy?.latestTransaction.status === TransactionStatus.ACTIVE
    ) {
      return (
        formatDate(policy?.latestTransaction?.effectiveDate) || TEXT_PLACEHOLDER
      );
    }

    return (
      formatDate(policy?.details?.policyExpirationDate) || TEXT_PLACEHOLDER
    );
  }, [policy?.details?.policyExpirationDate]);

  const firstNamedInsured = policy.firstNamedInsured;

  const assignedUser = useMemo(() => {
    return policy?.assignedTo?.firstName
      ? `${policy.assignedTo.firstName} ${policy.assignedTo.lastName}`
      : TEXT_PLACEHOLDER;
  }, [policy?.assignedTo?.firstName, policy?.assignedTo?.lastName]);

  const statusStage = useMemo(() => {
    if (!policy) {
      return TEXT_PLACEHOLDER;
    }

    // IF we are in a Midterm Transaction, show Pending: TRANSACTION
    if (
      policy?.latestTransaction?.transactionType !==
        TransactionType.POLICY_BOUND &&
      policy?.latestTransaction?.status !== TransactionStatus.ACTIVE
    ) {
      return 'PENDING: Midterm Transaction In Progress';
    }

    if (
      policy?.latestTransaction?.transactionType ===
      TransactionType.POLICY_CANCELLED
    ) {
      return 'Policy Cancelled';
    }

    const quoteType = policy?.details?.quoteType
      ? QuoteTypeToLabel[policy?.details?.quoteType]
      : policy?.details?.policyType
        ? PolicyTypeToLabel[policy?.details?.policyType]
        : TEXT_PLACEHOLDER;

    const latestTransaction = policy?.latestTransaction?.status
      ? TransactionStatusToLabel[policy.latestTransaction.status]
      : TEXT_PLACEHOLDER;

    return `${quoteType} / ${latestTransaction}`;
  }, [
    policy?.details?.quoteType,
    policy?.details?.policyType,
    policy?.latestTransaction?.status,
  ]);

  const prorationFactor = useMemo(() => {
    const response = latestRatingAttempt?.response as RatingResponse;
    return response?.metadata?.proration_str
      ? response.metadata?.proration_str
      : TEXT_PLACEHOLDER;
  }, [latestRatingAttempt?.response]);

  return (
    <header className="bg-base-100/80 pointer-events-none !text-[14px]">
      {/* backdrop-blur-md */}
      <section className="flex flex-col items-center justify-between sm:text-xs 2xl:text-[14px]">
        <div
          className={`pointer-events-auto mx-auto w-full overflow-hidden border-brand-200 ${isTravelTime ? 'bg-[#D4ECEE]' : 'bg-brand-100'} pl-10`}
        >
          <div
            className={`flex w-full ${isTravelTime ? 'gap-2' : 'justify-between gap-8'}`}
          >
            <div className="container flex justify-between sm:w-[85%] md:w-[85%]">
              <div className="flex w-4/12 flex-col gap-1 py-4 pr-8">
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">First Name Insured:</span>
                  <span
                    className="block w-7/12 overflow-hidden overflow-ellipsis whitespace-nowrap font-bold"
                    title={firstNamedInsured}
                  >
                    {getCleanText(firstNamedInsured)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Agency:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={policy?.details?.agencyName || 'agencyName'}
                  >
                    {getCleanText(policy?.details?.agencyName)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Assigned User:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={assignedUser}
                  >
                    {getCleanText(assignedUser)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Change Effective Date:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={transactionEffectiveDate || ''}
                  >
                    {getCleanText(transactionEffectiveDate)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Proration:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={prorationFactor || ''}
                  >
                    {getCleanText(prorationFactor)}
                  </span>
                </div>
              </div>

              <Separator className="my-4 h-auto" orientation="vertical" />

              <div className="flex w-4/12 flex-col gap-1 px-8 py-4">
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">
                    <span>Quote Number:</span>
                  </span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={policy?.policyNumber || ''}
                  >
                    {getCleanText(policy?.policyNumber)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Primary Practice State:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={policy?.details?.policyContractState || ''}
                  >
                    {getCleanText(policy?.details?.policyContractState)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Status/Stage:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={statusStage}
                  >
                    {getCleanText(statusStage)}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Policy Period</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-break-spaces font-bold"
                    title={`${effectiveDate} - ${expirationDate}`}
                  >
                    {effectiveDate} - {expirationDate}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Transaction Type</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-break-spaces font-bold"
                    title={snakeToTitleCase(
                      transactionTypeDisplayText(
                        policy?.latestTransaction?.transactionType,
                        policy?.latestTransaction?.status
                      )
                    )}
                  >
                    {snakeToTitleCase(
                      transactionTypeDisplayText(
                        policy?.latestTransaction?.transactionType,
                        policy?.latestTransaction?.status
                      )
                    )}
                  </span>
                </div>
              </div>

              <Separator className="my-4 h-auto" orientation="vertical" />

              <div className="flex w-4/12 flex-col gap-1 px-8 py-4">
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">
                    <span>Policy Number:</span>
                  </span>
                  <span
                    className="block flex w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title={policy?.boundPolicyNumber || ''}
                  >
                    {getCleanText(policy?.boundPolicyNumber)}
                    {policy &&
                      policy?.latestTransaction.status ===
                        TransactionStatus.ACTIVE && (
                        <span
                          className="text-ellipsis whitespace-nowrap pl-2 pt-0.5 font-bold"
                          title="Locked"
                          style={{ width: '15px' }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            width={15}
                          >
                            <path d="M144 144l0 48 160 0 0-48c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192l0-48C80 64.5 144.5 0 224 0s144 64.5 144 144l0 48 16 0c35.3 0 64 28.7 64 64l0 192c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 256c0-35.3 28.7-64 64-64l16 0z"></path>
                          </svg>
                        </span>
                      )}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">
                    <span>Transaction ID:</span>
                  </span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title="Transaction ID"
                  >
                    {getCleanText(policy?.transactionId)}
                  </span>
                </div>

                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Written Amount:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title="Written Premium"
                  >
                    {(policy?.latestTransaction?.writtenPremium || 0) +
                      (policy?.latestTransaction?.writtenTax || 0) ===
                    0
                      ? TEXT_PLACEHOLDER
                      : formatCurrency(
                          (policy?.latestTransaction?.writtenPremium || 0) +
                            (policy?.latestTransaction?.writtenTax || 0)
                        )}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Billed Amount:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title="Billed Amount"
                  >
                    {(policy?.latestTransaction?.billedPremium || 0) +
                      (policy?.latestTransaction?.billedTax || 0) ===
                    0
                      ? TEXT_PLACEHOLDER
                      : formatCurrency(
                          (policy?.latestTransaction?.billedPremium || 0) +
                            (policy?.latestTransaction?.billedTax || 0)
                        )}
                  </span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="block w-5/12">Annual Amount:</span>
                  <span
                    className="block w-7/12 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    title="Annual Amount"
                  >
                    {(policy?.latestTransaction?.annualPremium || 0) +
                      (policy?.latestTransaction?.annualTax || 0) ===
                    0
                      ? TEXT_PLACEHOLDER
                      : formatCurrency(
                          (policy?.latestTransaction?.annualPremium || 0) +
                            (policy?.latestTransaction?.annualTax || 0)
                        )}
                  </span>
                </div>
              </div>
            </div>

            {isTravelTime ? (
              <div className="flex flex-col items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start justify-self-start p-4"
                  onClick={() => {
                    sessionStorage.removeItem('sequence_id');
                    window.location.reload();
                  }}
                >
                  <History size={20} strokeWidth={1.5} className="rounded-sm" />
                  End Time Travel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start justify-self-start p-4"
                  disabled={loadingDebug}
                  onClick={() => {
                    setOpenDebugDialog(true);
                    setLoadingDebug(true);
                  }}
                >
                  Debug Previous Rating
                </Button>
              </div>
            ) : (
              <div className="flex w-2/12 justify-end">
                <Separator className="my-4 h-auto" orientation="vertical" />
                <div className="m-4 flex h-auto w-8/12 max-w-[100px] items-center justify-center">
                  <DropdownMenuButton
                    policy={policy}
                    setMessage={setMessage}
                    latestTransactionSummary={latestTransactionSummary}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <StandardMessageHeader message={message} />
        {submissionError && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white p-6">
              <h2 className="mb-4 text-xl font-bold text-red-600">
                Policy Issuance Failed
              </h2>
              {submissionError}
              <button
                type="button"
                className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                onClick={() => setSubmissionError(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </section>
      <Dialog open={openDebugDialog} onOpenChange={setOpenDebugDialog}>
        <DialogContent className="w-full max-w-[80vw] overflow-auto [&>button]:right-2 [&>button]:top-2">
          <DebugTransaction
            accountId={policy.pk!}
            setLoadingDebug={setLoadingDebug}
          />
        </DialogContent>
      </Dialog>
    </header>
  );
};
